import { useEffect, useState } from "react";

import {
  AssistantCommentWrapper,
  Choice,
  ChoicesWrapper,
  Recommend,
  RecommendLeft,
  RecommendsBackground,
  RecommendsWrapper,
} from "@/commands/AIConcierge/chatArea.style";
import { sampleConversationData } from "@/commands/AIConcierge/sampleData";
import { ConversationNode } from "@/commands/AIConcierge/types";
import { useConversation } from "@/commands/AIConcierge/useConversation";

type Props = {
  updateCharacterByPattern: (node: ConversationNode) => void;
  setNavigationHandlers: React.Dispatch<
    React.SetStateAction<{
      goToStart: () => void;
      goBack: () => void;
      canGoBack: boolean;
    } | null>
  >;
  isChoiceSelected: boolean;
  setIsChoiceSelected: React.Dispatch<React.SetStateAction<boolean>>;
  setShowButtonsWrapper: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCharacterComment: React.Dispatch<React.SetStateAction<boolean>>;
};

export function ChatArea({
  updateCharacterByPattern,
  setNavigationHandlers,
  isChoiceSelected,
  setIsChoiceSelected,
  setShowButtonsWrapper,
  setShowCharacterComment,
}: Props) {
  const {
    currentNode,
    canGoBack,
    isFinalAnswer,
    selectChoice,
    goBack,
    goToStart,
  } = useConversation(sampleConversationData);

  // フェード効果のためのstate
  const [showChoices, setShowChoices] = useState(false);

  // キャラクターの状態を更新（パターンベース）
  useEffect(() => {
    updateCharacterByPattern(currentNode);
  }, [currentNode, updateCharacterByPattern]);

  // ナビゲーションハンドラーを設定
  useEffect(() => {
    setNavigationHandlers({
      goToStart,
      goBack,
      canGoBack,
    });
  }, [goToStart, goBack, canGoBack, setNavigationHandlers]);

  // 選択肢の表示タイミング制御
  useEffect(() => {
    // 選択肢がクリックされた場合の処理
    const wasChoiceSelected = isChoiceSelected;
    if (isChoiceSelected) {
      setIsChoiceSelected(false);
      // ボタンは表示したままにする
    }

    // まず選択肢、コメントを非表示にする
    setShowChoices(false);
    setShowCharacterComment(false);

    // タイマーの時間を決定
    // 選択肢クリック後: キャラクターのコメントが800msでフェードイン完了後、さらに1000ms待つ = 1500ms
    // 初回表示時またはナビゲーション操作時: 1000ms
    const delay = wasChoiceSelected ? 1500 : 1000;

    // 選択肢の出現と同時にボタンも表示（選択肢クリック時はボタンは既に表示されているので、そのまま）
    const choicesTimer = setTimeout(() => {
      setShowChoices(true);
      setShowCharacterComment(true);
      // 選択肢クリック時はボタンは表示したままなので、setShowButtonsWrapperは呼ばない
      if (!wasChoiceSelected) {
        setShowButtonsWrapper(true);
      }
    }, delay);

    return () => {
      clearTimeout(choicesTimer);
    };
  }, [
    currentNode,
    isChoiceSelected,
    setIsChoiceSelected,
    setShowButtonsWrapper,
    setShowCharacterComment,
  ]);

  const handleChoiceClick = (index: number) => {
    // 選択肢がクリックされたことを記録
    setIsChoiceSelected(true);
    // フェードアウトなしで即座に非表示
    setShowChoices(false);

    // 500ms後に選択肢を実行
    setTimeout(() => {
      selectChoice(index);
    }, 500);
  };

  return (
    <AssistantCommentWrapper>
      {isFinalAnswer ? (
        <RecommendsBackground
          style={{
            opacity: showChoices ? 1 : 0,
            transition: isChoiceSelected ? "none" : "opacity 0.5s ease-in-out",
          }}
        >
          <RecommendsWrapper>
            {currentNode.choices.map((choice) => {
              // 「・」で始まる選択肢は中央揃え、それ以外は左寄せボックス組み
              const isCentered = choice.trim().startsWith("・");
              const RecommendComponent = isCentered ? Recommend : RecommendLeft;
              return (
                <RecommendComponent key={choice}>{choice}</RecommendComponent>
              );
            })}
          </RecommendsWrapper>
        </RecommendsBackground>
      ) : (
        <ChoicesWrapper
          style={{
            opacity: showChoices ? 1 : 0,
            transition: isChoiceSelected ? "none" : "opacity 0.5s ease-in-out",
          }}
        >
          {currentNode.choices.map((choice) => (
            <Choice
              key={choice}
              onClick={() => {
                handleChoiceClick(currentNode.choices.indexOf(choice));
              }}
            >
              {choice}
            </Choice>
          ))}
        </ChoicesWrapper>
      )}
    </AssistantCommentWrapper>
  );
}
